<template>
    <div class="mt-4">
        <template v-if="submitted">
            <div v-if="checkoutSession.type === 'woocommerce-link'">
                Du skickas automatiskt vidare. Om inget händer,
                <a :href="checkoutSession.url">klicka här för att gå vidare</a>.
            </div>

            <div v-if="checkoutSession.type === 'dintero'">
                Du skickas automatiskt vidare till Dintero. Om inget händer,
                <a :href="checkoutSession.url">klicka här för att gå vidare</a>.
            </div>

            <div
                v-if="checkoutSession.type === 'briqpay'"
                id="briqpay"
                class="briqpay-container"
            >
                <iframe
                    id="briqpay-iframe"
                    title="Checkout"
                    scrolling="no"
                    :src="`https://msc.briqpay.com?sessionToken=${checkoutSession.token}`"
                ></iframe>
            </div>

            <div
                v-if="checkoutSession.type === 'svea-checkout'"
                ref="sveaCheckout"
                class="svea-container"
            >
                <div
                    id="svea-checkout-spinner"
                    v-if="!checkoutShow"
                    class="d-flex justify-content-center align-items-center"
                    style="min-height: inherit"
                >
                    <i
                        class="fa-solid fa-circle-notch fa-spin fa-3x"
                        style="color: var(--primary-color)"
                    ></i>
                </div>
                <div
                    ref="sveaCheckoutContainer"
                    v-show="checkoutShow"
                    style="min-height: inherit"
                ></div>
            </div>
            <div
                v-if="checkoutSession.type === 'klarna-checkout'"
                ref="klarnaCheckout"
                class="klarna-container"
            >
                <div
                    id="klarna-checkout-spinner"
                    v-if="!checkoutShow"
                    class="d-flex justify-content-center align-items-center"
                    style="min-height: inherit; color: red"
                >
                    <i
                        class="fa-solid fa-circle-notch fa-spin fa-3x"
                        style="color: var(--primary-color)"
                    ></i>
                </div>
                <div
                    ref="klarnaCheckoutContainer"
                    v-show="checkoutShow"
                    style="min-height: inherit"
                ></div>
            </div>

            <div v-if="checkoutSession.type === 'zaver'">
                Du skickas automatiskt vidare till Zaver. Om inget händer,
                <a :href="checkoutSession.url">klicka här för att gå vidare</a>.
            </div>

            <div v-if="checkoutSession.type === 'shopify-checkout'">
                Du skickas automatiskt vidare till Shopify. Om inget händer,
                <a :href="checkoutSession.url">klicka här för att gå vidare</a>.
            </div>

            <div v-if="checkoutSession.type === 'swish-recurring'">
                <swish-recurring-checkout
                    :quoteKey="quoteKey"
                    :productId="productId"
                    :paymentRequestId="paymentRequestId"
                    :initialPhoneNumber="initialPhoneNumber"
                    :payload="swishRecurringPayload"
                    v-model="swishRecurringAlias"
                    @submitted="handleSwishRecurringSubmit"
                    @success="handleSwishRecurringSuccess"
                    @failed="handleSwishRecurringFailed"
                />
            </div>
        </template>
        <form v-else @submit.prevent="submit">
            <div v-if="isLocalhost" class="my-5">
                <h6>DEBUG</h6>
                <p>Item ID: {{ productId }}</p>
            </div>

            <button
                v-if="!submitting"
                type="submit"
                class="signatureComponentSignButton btn btn-secondary mb-3"
                :disabled="!hasData"
            >
                <i class="fas fa-paper-plane mr-1"></i>
                {{ $t("quotePayment.product.go_to_payment_button") }}
            </button>
            <button
                v-else
                type="button"
                class="signatureComponentSignButton btn btn-primary mt-3 wait"
            >
                <i class="fas fa-paper-plane mr-2"></i>
                <div class="spinner-border spinner-border-sm"></div>
            </button>
        </form>
        <img
            v-if="dinteroBrandingImageUrl"
            class="d-block w-75 h-auto mx-auto my-4"
            :src="dinteroBrandingImageUrl"
        />
    </div>
</template>

<style lang="scss" scoped>
button {
    display: block;
    margin-left: auto;
    margin-right: auto;
    min-width: 33%;

    &.wait .spinner-border {
        vertical-align: middle;
    }
}

div.briqpay-container,
div.svea-container,
div.klarna-container {
    min-height: 400px;

    iframe {
        width: 100%;
        min-height: 400px;
        border: none;
    }
}
</style>

<script setup>
import axios from "axios";
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { route } from "ziggy";

const props = defineProps({
    quoteKey: { type: String, required: true },
    quoteType: { type: String, required: true },
    productId: { type: Number, required: true },
    paymentMethod: { type: String, required: true },
    dinteroProfileId: { type: String, default: null },
    signeeNameIsValidated: { type: Boolean, default: false },
    signeeName: { type: String, default: null },
    itemDescription: { type: String, default: null },
    initialPhoneNumber: { type: String, default: null },
});

const submitting = ref(false);
const submitted = ref(false);
const checkoutSession = ref({});
const checkoutLoadingInterval = ref(null);
const checkoutShow = ref(false);
const checkoutInterval = ref(null);
const checkoutRedirectTimeout = ref(null);
const paymentRequestId = ref(null);
const swishRecurringAlias = ref(null);

const sveaCheckout = ref(null);
const sveaCheckoutContainer = ref(null);
const klarnaCheckout = ref(null);
const klarnaCheckoutContainer = ref(null);

const isLocalhost = computed(() => document.location.hostname === "localhost");

const dinteroBrandingImageUrl = computed(() => {
    if (props.paymentMethod === "dintero" && props.dinteroProfileId) {
        return `https://backoffice.dintero.com/checkouts/v1/branding/profiles/${props.dinteroProfileId}/variant/colors/width/420/dintero_top_frame.svg`;
    }

    return null;
});

// The signer name is no longer collected here — the server fills it
// from the quote's customer_name — so the customer can always
// proceed straight to payment.
const hasData = computed(() => true);

const url = computed(() => {
    if (props.quoteType === "options") {
        return route("quote.options.payments.store", [
            props.quoteKey,
            props.productId,
        ]);
    }

    return route("quote.products.payments.store", [
        props.quoteKey,
        props.productId,
    ]);
});

const swishRecurringPayload = computed(() => ({
    quoteKey: props.quoteKey,
    productId: props.productId,
    paymentRequestId: paymentRequestId.value,
    checkoutSession: checkoutSession.value,
}));

function handleCheckoutReady() {
    checkoutShow.value = true;
}

function clearCheckoutResources() {
    clearInterval(checkoutLoadingInterval.value);
    clearInterval(checkoutInterval.value);
    clearTimeout(checkoutRedirectTimeout.value);
    checkoutLoadingInterval.value = null;
    checkoutInterval.value = null;
    checkoutRedirectTimeout.value = null;
    document.removeEventListener("checkoutReady", handleCheckoutReady);
}

async function resetState() {
    clearCheckoutResources();
    submitted.value = false;
    submitting.value = false;
    checkoutSession.value = {};
    checkoutLoadingInterval.value = null;
    checkoutShow.value = false;
    paymentRequestId.value = null;

    if (props.signeeNameIsValidated) {
        await submit();
    }
}

async function submit(payload = null) {
    // We are going to fake a payment session initiation until the alias is provided.
    if (
        props.paymentMethod === "swish-recurring" &&
        !swishRecurringAlias.value
    ) {
        submitted.value = true;
        paymentRequestId.value = null;
        checkoutSession.value = {
            type: "swish-recurring",
            token: null,
            status: "INITIATING",
        };
        return;
    }

    try {
        const productId = props.productId;

        submitting.value = true;

        const response = await axios.post(url.value, {
            signature_item_description: props.itemDescription,
            payment_method: props.paymentMethod,
            payload: payload,
        });

        if (productId !== props.productId) {
            await resetState();
            return;
        }

        submitted.value = true;
        paymentRequestId.value = response.data.id;
        checkoutSession.value = response.data.checkout_session;
        switch (checkoutSession.value.type) {
            case "dintero":
            case "zaver":
            case "woocommerce-link":
            case "shopify-checkout":
                window.location.replace(checkoutSession.value.url);
                break;

            case "briqpay":
                // noop
                break;

            case "swish-recurring":
                // noop
                break;
            case "svea-checkout":
                // noop
                break;
            case "klarna-checkout":
                // noop
                break;

            default:
                throw new Error(
                    `Unknown checkout session type ${checkoutSession.value.type}`
                );
        }
    } catch (error) {
        console.warn("Failed to create payment request..", error);
        if (props.paymentMethod === "swish-recurring") {
            checkoutSession.value = {
                type: "swish-recurring",
                token: null,
                status: "FAILED",
            };
        }
    } finally {
        submitting.value = false;
    }
}

function handleSwishRecurringSuccess(paymentRequest) {
    clearTimeout(checkoutRedirectTimeout.value);
    checkoutRedirectTimeout.value = setTimeout(() => {
        window.location.replace(paymentRequest.return_url);
    }, 5 * 1000);
}

function handleSwishRecurringSubmit() {
    submit({
        swishRecurringAlias: swishRecurringAlias.value,
    });
}

function handleSwishRecurringFailed() {
    paymentRequestId.value = null;
    checkoutSession.value = {
        type: "swish-recurring",
        token: null,
        status: "INITIATING",
    };
}

watch(
    () => props.productId,
    async () => {
        if (!submitting.value) {
            await resetState();
        }
    }
);

watch(checkoutSession, (value) => {
    if (value && value.type === "svea-checkout") {
        const fragment = document
            .createRange()
            .createContextualFragment(value.gui_snippet);
        if (checkoutLoadingInterval.value) {
            return;
        }

        checkoutLoadingInterval.value = setInterval(() => {
            if (!sveaCheckout.value) {
                return;
            }

            sveaCheckoutContainer.value.innerHTML = "";
            sveaCheckoutContainer.value.appendChild(fragment);
            clearInterval(checkoutLoadingInterval.value);
            checkoutLoadingInterval.value = null;
        }, 100);

        document.removeEventListener("checkoutReady", handleCheckoutReady);
        document.addEventListener("checkoutReady", handleCheckoutReady);
    }

    if (value && value.type === "klarna-checkout") {
        const fragment = document
            .createRange()
            .createContextualFragment(value.gui_snippet);
        if (checkoutLoadingInterval.value) {
            return;
        }

        checkoutLoadingInterval.value = setInterval(() => {
            if (!klarnaCheckout.value) {
                return;
            }
            const checkoutContainer = klarnaCheckoutContainer.value;
            checkoutContainer.innerHTML = "";
            checkoutContainer.innerHTML = value.gui_snippet;
            const scriptsTags =
                checkoutContainer.getElementsByTagName("script");
            // This is necessary otherwise the scripts tags are not going to be evaluated
            for (let i = 0; i < scriptsTags.length; i++) {
                const parentNode = scriptsTags[i].parentNode;
                const newScriptTag = document.createElement("script");
                newScriptTag.type = "text/javascript";
                newScriptTag.text = scriptsTags[i].text;
                parentNode.removeChild(scriptsTags[i]);
                parentNode.appendChild(newScriptTag);
            }

            clearInterval(checkoutLoadingInterval.value);
            checkoutLoadingInterval.value = null;
            checkoutShow.value = true;
        }, 100);
    }
});

onMounted(async () => {
    await resetState();
});

onBeforeUnmount(clearCheckoutResources);
</script>
